import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ContactsEditor from "@/components/ContactsEditor";
import ActivityLog from "@/components/ActivityLog";
import CategoryMultiSelect from "@/components/CategoryMultiSelect";
import IndustrySelect from "@/components/IndustrySelect";
import DivisionsEditor from "@/components/DivisionsEditor";
import DraftBanner from "@/components/DraftBanner";
import SupplierDocuments from "@/components/SupplierDocuments";
import VisibilityToggle from "@/components/VisibilityToggle";
import AuthStatusChip from "@/components/AuthStatusChip";
import OemQuoteProfileCard from "@/components/OemQuoteProfileCard";
import { useDraftGuard } from "@/hooks/useDraftGuard";
import { SUPPLIER_CATEGORIES, labelFor } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Save, Loader2, History as HistoryIcon, Package, Repeat } from "lucide-react";
import { toast } from "sonner";

const statusColor = (s) => ({
  awarded: "bg-emerald-100 text-emerald-700", quoted: "bg-[#E6ECF5] text-[#1B4383]",
  sent: "bg-slate-100 text-slate-600", declined: "bg-rose-100 text-rose-700",
}[s] || "bg-slate-100 text-slate-600");

export default function SupplierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState(null);
  const [involvement, setInvolvement] = useState([]);
  const [saving, setSaving] = useState(false);
  const [activityKey, setActivityKey] = useState(0);
  const [serverContacts, setServerContacts] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/suppliers/${id}`);
      setForm({ ...data, categories: data.categories || (data.category ? [data.category] : []), contacts: data.contacts || [], notes: data.notes || "" });
      setServerContacts((data.contacts || []).length);
      api.get(`/suppliers/${id}/history`).then((r) => setInvolvement(r.data.involvement || [])).catch(() => {});
    } catch (e) {
      toast.error("This supplier is private — you don't have access to its profile.");
      navigate("/suppliers");
    }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const buildPayload = (extra = {}) => {
    const primary = (form.contacts || []).find((c) => c.primary) || (form.contacts || [])[0] || {};
    return {
      name: form.name, industry: form.industry || "", categories: form.categories || [],
      partner_type: form.partner_type || "supplier", authorized: (form.auth?.status || "none") !== "none",
      supplier_type: form.supplier_type || "international",
      contact_person: primary.name || "", contact_email: primary.email || "",
      phone: form.phone || "", website: form.website || "", tin: form.tin || "", vrn: form.vrn || "", address: form.address || "",
      notes: form.notes || "", contacts: form.contacts || [], divisions: form.divisions || [],
      visibility: form.visibility || "team", ...extra,
    };
  };

  const save = async () => {
    // Guard against accidentally wiping saved contacts on an unrelated edit.
    const nowCount = (form.contacts || []).length;
    let clear_contacts = false;
    if (nowCount === 0 && serverContacts > 0) {
      if (!window.confirm(`This will remove all ${serverContacts} saved contact(s) for this supplier. Continue?`)) return;
      clear_contacts = true;
    }
    setSaving(true);
    try {
      await api.put(`/suppliers/${id}`, buildPayload({ clear_contacts }));
      toast.success("Supplier saved");
      guard.markSaved();
      setActivityKey((k) => k + 1);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const isOem = (form?.partner_type || "supplier") === "oem";
  const [converting, setConverting] = useState(false);
  const convert = async () => {
    const target = isOem ? "supplier" : "oem";
    if (!window.confirm(isOem ? "Move this OEM partner back to Suppliers?" : "Convert this supplier into an OEM partner?")) return;
    setConverting(true);
    try {
      await api.put(`/suppliers/${id}`, buildPayload({ partner_type: target, authorized: target === "oem" ? !!form.authorized : false }));
      toast.success(target === "oem" ? "Moved to OEM Partners" : "Moved to Suppliers");
      guard.markSaved();
      navigate("/suppliers");
    } catch (e) { toast.error(apiError(e)); } finally { setConverting(false); }
  };

  const editable = form ? {
    name: form.name, categories: form.categories || [], partner_type: form.partner_type || "supplier",
    phone: form.phone || "", website: form.website || "",
    tin: form.tin || "", vrn: form.vrn || "", address: form.address || "", notes: form.notes || "",
    contacts: form.contacts || [], divisions: form.divisions || [], visibility: form.visibility || "team",
  } : null;
  const guard = useDraftGuard(`supplier:${id}`, editable, { enabled: !!form });
  const restoreDraft = () => { const d = guard.restore(); if (d) setForm((f) => ({ ...f, ...d })); };
  const categoryOptions = (form?.categories || []).map((c) => ({ value: c, label: labelFor(c, SUPPLIER_CATEGORIES) }));

  if (!form) return <AppLayout title="Supplier"><div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div></AppLayout>;

  return (
    <AppLayout title="Supplier Detail">
      <button onClick={() => navigate("/suppliers")} className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary" data-testid="back-to-suppliers">
        <ArrowLeft className="h-4 w-4" /> Back to Suppliers
      </button>

      {guard.recovered && <DraftBanner onRestore={restoreDraft} onDiscard={guard.discard} />}

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div>
          <div className="text-xs font-semibold tracking-wide text-brand-soft">{isOem ? "OEM Partner" : "Supplier"}</div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl" data-testid="supplier-detail-name">{form.name}</h2>
          {form.website && (
            <a href={form.website.startsWith("http") ? form.website : `https://${form.website}`} target="_blank" rel="noreferrer"
              className="mt-1 inline-block text-sm text-[#1B4383] hover:underline" data-testid="supplier-detail-website-link">{form.website}</a>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={convert} disabled={converting} data-testid="convert-partner-type" className="flex-1 sm:flex-none">
            {converting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Repeat className="mr-2 h-4 w-4" />}
            {isOem ? "Move to Suppliers" : "Convert to OEM Partner"}
          </Button>
          <Button onClick={save} disabled={saving} data-testid="supplier-save-changes" className="flex-1 sm:flex-none">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save Changes
          </Button>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        {isAdmin && <VisibilityToggle value={form.visibility || "team"} onChange={(v) => set({ visibility: v })} />}
        <div className="inline-flex overflow-hidden rounded-md border border-border" data-testid="supplier-type-toggle">
          {[["local", "Local"], ["international", "International"]].map(([v, lbl]) => (
            <button key={v} onClick={() => set({ supplier_type: v })}
              className={`px-3 py-1.5 text-xs font-medium ${(form.supplier_type || "international") === v ? "bg-[#1B4383] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              data-testid={`supplier-type-${v}`}>{lbl}</button>
          ))}
        </div>
        {isOem && form.auth && <AuthStatusChip auth={form.auth} />}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-lg border border-border bg-white p-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Company Name</Label>
                <Input value={form.name} onChange={(e) => set({ name: e.target.value })} data-testid="supplier-detail-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Categories</Label>
                <CategoryMultiSelect value={form.categories || []} onChange={(v) => set({ categories: v })} testid="supplier-detail-category" />
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Industry</Label>
                <IndustrySelect value={form.industry || ""} onChange={(v) => set({ industry: v })} testid="supplier-detail-industry" /></div>
              {(form.brands || []).length > 0 && (
                <div className="space-y-1.5 sm:col-span-2" data-testid="supplier-detail-brands"><Label className="text-xs">Brands Supplied</Label>
                  <div className="flex flex-wrap gap-1.5">{form.brands.map((b) => <span key={b} className="rounded-full bg-[#E6ECF5] px-2.5 py-0.5 text-xs font-semibold text-[#1B4383]">{b}</span>)}</div></div>
              )}
              <div className="space-y-1.5"><Label className="text-xs">Phone</Label>
                <Input value={form.phone || ""} onChange={(e) => set({ phone: e.target.value })} data-testid="supplier-detail-phone" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Website</Label>
                <Input placeholder="https://example.com" value={form.website || ""} onChange={(e) => set({ website: e.target.value })} data-testid="supplier-detail-website" /></div>
              <div className="space-y-1.5"><Label className="text-xs">TIN</Label>
                <Input value={form.tin || ""} onChange={(e) => set({ tin: e.target.value })} data-testid="supplier-detail-tin" /></div>
              <div className="space-y-1.5"><Label className="text-xs">VRN</Label>
                <Input value={form.vrn || ""} onChange={(e) => set({ vrn: e.target.value })} data-testid="supplier-detail-vrn" /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Address</Label>
                <Input value={form.address || ""} onChange={(e) => set({ address: e.target.value })} data-testid="supplier-detail-address" /></div>
              {isOem && (
                <div className="flex items-center gap-2 sm:col-span-2 rounded-md border border-border bg-slate-50 px-3 py-2.5" data-testid="oem-authorized-toggle">
                  <input type="checkbox" checked={(form.auth?.status || "none") !== "none"} disabled readOnly className="h-4 w-4 accent-[#1B4383]" data-testid="oem-authorized-checkbox" />
                  <span className="text-sm text-slate-700">
                    {(form.auth?.status || "none") !== "none"
                      ? "Authorisation letter on file (auto-detected from uploads below)"
                      : "No authorisation letter yet — upload one below to authorise this OEM"}
                  </span>
                </div>
              )}
              <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Notes</Label>
                <Textarea rows={3} placeholder="Account notes, terms, history…" value={form.notes || ""} onChange={(e) => set({ notes: e.target.value })} data-testid="supplier-detail-notes" /></div>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-white p-6">
            <h3 className="mb-4 font-heading text-sm font-bold tracking-tight">Contact Persons</h3>
            <ContactsEditor
              company={form.name}
              contacts={form.contacts}
              onChange={(c) => {
                const extraCats = Array.from(new Set(c.flatMap((x) => x.divisions || [])));
                const cats = Array.from(new Set([...(form.categories || []), ...extraCats]));
                set({ contacts: c, categories: cats });
              }}
              showAlwaysInclude
              showDivisions
            />
          </div>

          <div className="rounded-lg border border-border bg-white p-6">
            <h3 className="mb-1 font-heading text-sm font-bold tracking-tight">Divisions & Recipients</h3>
            <p className="mb-4 text-xs text-muted-foreground">For each category, choose the To person and CC people from your contacts. When you send an RFQ, the matching division's recipients are picked automatically.</p>
            <DivisionsEditor value={form.divisions || []} categoryOptions={categoryOptions} contacts={form.contacts || []} onChange={(d) => set({ divisions: d })} />
          </div>

          {isOem && <OemQuoteProfileCard supplierId={id} supplierName={form.name} />}
          {isOem && <SupplierDocuments supplierId={id} onExpiryChange={load} />}
        </div>

        <div className="rounded-lg border border-border bg-white p-6" data-testid="supplier-360-history">
          <div className="mb-4 flex items-center gap-2">
            <HistoryIcon className="h-4 w-4 text-[#1B4383]" />
            <h3 className="font-heading text-sm font-bold tracking-tight">360° History</h3>
          </div>
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold tracking-wide text-slate-400"><Package className="h-3.5 w-3.5" />RFQ Involvement</div>
          <div className="space-y-2">
            {involvement.length ? involvement.map((v) => (
              <Link to={`/rfqs/${v.rfq_id}`} key={v.id} className="block rounded-md border border-border p-2.5 hover:bg-slate-50" data-testid={`supplier-inv-${v.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs text-[#1B4383]">{v.rfq_code || "—"}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${statusColor(v.status)}`}>{v.status}</span>
                </div>
                <div className="mt-1 truncate text-sm text-slate-800">{v.item || v.rfq_title}</div>
                {v.quoted_price != null && (
                  <div className="mt-0.5 text-xs text-muted-foreground">{(v.currency || "").toUpperCase()} {Number(v.quoted_price).toLocaleString()}</div>
                )}
              </Link>
            )) : <p className="text-xs text-muted-foreground">No RFQ involvement yet.</p>}
          </div>
        </div>
      </div>

      <div className="mt-6">
        <ActivityLog entityType="suppliers" entityId={id} refreshKey={activityKey} />
      </div>
    </AppLayout>
  );
}
